<script setup lang="ts">
import { computed } from 'vue';
import { useProductPage } from '../../../business/use-product-page';
import { useProductModals } from '../../../business/useProductModals';
import {
  IconPaymentButtonDolyame,
  IconPaymentButtonPodeli,
  IconChevronRightLarge,
} from '@/features/ui-kit/icons';
import { EProductOptionDtoTypes } from '@/pages/index/-src/shared/types/product';
import type { IProductOptionDto } from '@/pages/index/-src/shared/types/product';
import { useTracker } from '~/features/clickstream-sdk/composables/useTracker';
import { ETrackerActions } from '~/features/clickstream-sdk/ClickStreamTracker';
import { ETrackerPages } from '~/features/clickstream-sdk/models/pages';

const { state } = useProductPage();
const { openBnplModal } = useProductModals();
const { track } = useTracker({ page: ETrackerPages.PRODUCT });

// Константы для диапазонов цен BNPL (в копейках)
const MIN_PRICE = 10000; // 100 ₽
const MAX_PRICE_PODELI = 3000000; // 30 000 ₽
const MAX_PRICE_DOLYAME = 10000000; // 100 000 ₽

// Функция для проверки, является ли опция BNPL-сервисом
const isBnplService = (option: IProductOptionDto): boolean => {
  return option.type === EProductOptionDtoTypes.DOLYAME || option.type === EProductOptionDtoTypes.PODELI;
};

// Функция для проверки, попадает ли цена SKU в допустимый диапазон для BNPL
const isPriceInRange = (sku: typeof state.skuList[0]): boolean => {
  if (!sku || !sku.purchasePrice) return false;

  const price = sku.purchasePrice;
  if (price < MIN_PRICE) return false;

  // Находим BNPL-опцию для этого SKU
  const bnplOption = sku.productOptionDtos?.find(isBnplService);
  if (!bnplOption) return false;

  // Проверяем максимальную цену в зависимости от типа
  const maxPrice =
    bnplOption.type === EProductOptionDtoTypes.PODELI ? MAX_PRICE_PODELI : MAX_PRICE_DOLYAME;

  return price <= maxPrice;
};

const currentSku = computed(() => state.currentSku);

const activeBNPLOption = computed((): IProductOptionDto | null => {
  if (!currentSku.value?.productOptionDtos) return null;

  return currentSku.value.productOptionDtos.find(isBnplService) || null;
});

const optionInfo = computed(() => {
  if (!activeBNPLOption.value) return null;

  return state.productOptionInfo.get(activeBNPLOption.value.optionId) || null;
});

// Проверка наличия BNPL-опций: плашка показывается, если есть хотя бы один SKU,
// у которого availableAmount > 0, productOptionDtos содержит BNPL-опцию и цена в допустимом диапазоне
const isBNPLShown = computed(() => {
  return state.skuList.some(
    (sku) => sku.availableAmount > 0 && sku.productOptionDtos?.find(isBnplService) && isPriceInRange(sku)
  );
});

const isDolyame = computed(() => {
  return optionInfo.value?.dolyamePaymentDates && optionInfo.value.dolyamePaymentDates.length > 0;
});

const isPodeli = computed(() => {
  return optionInfo.value?.podeliPaymentDates && optionInfo.value.podeliPaymentDates.length > 0;
});

// Кнопка отключается, если:
// - activeSKU.availableAmount <= 0 (нет в наличии)
// - active === false (опция неактивна для текущего SKU)
// - цена не попадает в допустимый диапазон
const isDisabled = computed(() => {
  if (!currentSku.value) return true;

  return (
    currentSku.value.availableAmount <= 0 ||
    currentSku.value.productOptionDtos?.find(isBnplService)?.active === false ||
    !isPriceInRange(currentSku.value)
  );
});

const bannerText = computed(() => {
  if (!optionInfo.value) return '';

  const paymentsCount = isPodeli.value
    ? optionInfo.value.podeliPaymentDates?.length
    : optionInfo.value.dolyamePaymentDates?.length;

  const price = parseInt(optionInfo.value.price);

  return isPodeli.value
    ? `на ${paymentsCount} платежа по ${price} ₽`
    : `${paymentsCount} платежа по ${price} ₽`;
});

const handleClick = () => {
  if (isDisabled.value) return;

  track({
    element: 'bnplBanner',
    action: ETrackerActions.CLICK,
    widget_name: isDolyame.value ? 'DolyamePopupOpen' : 'PodeliPopupOpen',
  });

  if (!optionInfo.value) return;

  openBnplModal(optionInfo.value);
};
</script>

<template>
  <div
    v-if="isBNPLShown && optionInfo"
    class="bnpl-banner"
    :class="{ disabled: isDisabled }"
    @click="handleClick"
  >
    <div class="bnpl-banner-content">
      <div
        class="bnpl-banner-icon"
        :class="{ 'bnpl-banner-icon--dolyame': isDolyame, 'bnpl-banner-icon--podeli': isPodeli }"
      >
        <IconPaymentButtonDolyame v-if="isDolyame" />
        <IconPaymentButtonPodeli v-else-if="isPodeli" />
      </div>

      <div class="bnpl-banner-text">
        {{ bannerText }}
      </div>

      <div class="bnpl-banner-arrow">
        <IconChevronRightLarge :size="24" color="tertiary" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="stylus">
.bnpl-banner
  width 100%
  margin-top 16px

  &.disabled
    opacity 0.6
    cursor not-allowed

  .bnpl-banner-content
    position relative
    padding 12px 8px 12px 12px
    background #F5F6FA
    border-radius 12px
    display flex
    align-items center
    cursor pointer
    transition background 0.15s ease

    &:hover:not(.disabled)
      background #EDEFF2

  .bnpl-banner-icon
    text-align center
    border-radius 7px
    border none
    width 82px
    display flex
    align-items center
    justify-content center
    height 28px

    &--dolyame
      background-color #000

    &--podeli
      background-color #ee3124

    :deep(svg)
      width 69px

  .bnpl-banner-text
    margin-left 8px
    font-size 14px
    line-height 18px
    font-weight 500
    color #141415
    flex 1

  .bnpl-banner-arrow
    margin-left auto
    display flex
    align-items center
</style>

